import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import accountsApi from "../../services/accountsCommonService";
import AccountsConstants from "../../common/AccountsConstants";
import { formatINR, ledgerIdOf, todayInput } from "./accountUser";
import LedgerCombo from "./LedgerCombo";
import { ApiAlert } from "./AccountWidgets";
import PageShell from "./PageShell";
import useAccountMeta from "./useAccountMeta";
import "./accounts.css";

const CASH_BANK = ["BANK", "CASH"];

export const ENTRY_CONFIG = {
  RECEIPT: {
    title: "Receipt Voucher",
    label: "Receipt",
    modeLabel: "Receipt Mode",
    debit: { label: "Bank / Cash Ledger (Dr)", groups: CASH_BANK },
    credit: { label: "Party / Income Ledger (Cr)", exclude: CASH_BANK },
  },
  PAYMENT: {
    title: "Payment Voucher",
    label: "Payment",
    modeLabel: "Payment Mode",
    debit: { label: "Party / Expense Ledger (Dr)", exclude: CASH_BANK },
    credit: { label: "Bank / Cash Ledger (Cr)", groups: CASH_BANK },
  },
  PURCHASE: {
    title: "Purchase Voucher",
    label: "Purchase",
    debit: { label: "Purchase / Expense Ledger (Dr)", groups: ["EXPENSE", "ASSET"] },
    credit: { label: "Supplier Ledger (Cr)", groups: ["SUNDRY_CREDITOR"] },
  },
  CREDIT_NOTE: {
    title: "Credit Note",
    label: "Credit note",
    debit: { label: "Sales / Discount Ledger (Dr)", groups: ["INCOME", "EXPENSE"] },
    credit: { label: "Customer Ledger (Cr)", groups: ["SUNDRY_DEBTOR"] },
  },
  DEBIT_NOTE: {
    title: "Debit Note",
    label: "Debit note",
    debit: { label: "Customer Ledger (Dr)", groups: ["SUNDRY_DEBTOR"] },
    credit: { label: "Income Ledger (Cr)", groups: ["INCOME"] },
  },
  JOURNAL: {
    title: "Journal Voucher",
    label: "Journal",
    debit: { label: "Debit Ledger (Dr)", exclude: CASH_BANK },
    credit: { label: "Credit Ledger (Cr)", exclude: CASH_BANK },
  },
};

const amountOk = (text) => /^\d+(\.\d{1,2})?$/.test(String(text).trim()) && Number(text) > 0;

function matches(ledger, side) {
  if (side.groups) return side.groups.includes(ledger.group);
  if (side.exclude) return !side.exclude.includes(ledger.group);
  return true;
}

function mapLineError(fieldErrors) {
  const debit = fieldErrors["lines[0].ledgerId"] || "";
  const credit = fieldErrors["lines[1].ledgerId"] || "";
  const amount = fieldErrors["lines[0].debit"] || fieldErrors["lines[1].credit"] || fieldErrors["lines[0]"] || fieldErrors["lines[1]"] || "";
  const preview = fieldErrors.lines || "";
  return { debit, credit, amount, preview };
}

function moneyQuery(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return "";
  return number.toFixed(2);
}

export function settlementLink(voucherType, row) {
  if (row.paymentStatus === "PAID" || row.status === "CANCELLED") return "";
  const open = moneyQuery(row.openAmount != null ? row.openAmount : (voucherType === "PURCHASE" ? row.totalCredit : row.totalDebit));
  if (!open) return "";
  if (voucherType === "SALES") {
    const party = (row.lines || []).find((line) => Number(line.debit) > 0);
    if (!party) return "";
    const params = new URLSearchParams({
      from: "invoice",
      referenceNo: row.voucherNo || "",
      creditLedgerId: party.ledgerId,
      amount: open,
    });
    return `${AccountsConstants.RECEIPT_CREATE}?${params.toString()}`;
  }
  if (voucherType === "PURCHASE") {
    const party = (row.lines || []).find((line) => Number(line.credit) > 0);
    if (!party) return "";
    const params = new URLSearchParams({
      from: "purchase",
      referenceNo: row.voucherNo || "",
      debitLedgerId: party.ledgerId,
      amount: open,
    });
    return `${AccountsConstants.PAYMENT_CREATE}?${params.toString()}`;
  }
  return "";
}

export default function VoucherEntry({ voucherType }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const linkedFrom = params.get("from") || "";
  const config = ENTRY_CONFIG[voucherType];
  const { meta, error: metaError } = useAccountMeta();
  const [ledgers, setLedgers] = useState([]);
  const [form, setForm] = useState({
    voucherDate: todayInput(),
    referenceNo: params.get("referenceNo") || "",
    paymentMode: "",
    narration: "",
    debitLedgerId: params.get("debitLedgerId") || "",
    creditLedgerId: params.get("creditLedgerId") || "",
    amount: params.get("amount") || "",
  });
  const [saved, setSaved] = useState(null);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    accountsApi.ledgers({ isActive: "true" })
      .then((result) => setLedgers(result?.data || []))
      .catch((err) => setError(err));
  }, [voucherType]);

  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const debitLedgers = ledgers.filter((row) => matches(row, config.debit));
  const creditLedgers = ledgers.filter((row) => matches(row, config.credit));
  const amount = amountOk(form.amount) ? form.amount.trim() : "0";
  const lines = useMemo(() => {
    const nameOf = (id) => ledgers.find((row) => ledgerIdOf(row) === String(id))?.name || "";
    return [
      { ledgerId: form.debitLedgerId, ledgerName: nameOf(form.debitLedgerId), debit: amount, credit: "0" },
      { ledgerId: form.creditLedgerId, ledgerName: nameOf(form.creditLedgerId), debit: "0", credit: amount },
    ];
  }, [form.debitLedgerId, form.creditLedgerId, amount, ledgers]);

  const sameLedger = form.debitLedgerId && form.debitLedgerId === form.creditLedgerId;
  const clientErrors = {
    voucherDate: !form.voucherDate ? "Date is required" : form.voucherDate > todayInput() ? "Date cannot be in the future" : "",
    paymentMode: config.modeLabel && !form.paymentMode ? "Select a mode" : "",
    debitLedgerId: !form.debitLedgerId ? "Select a ledger" : sameLedger ? "Debit and credit ledgers must be different" : "",
    creditLedgerId: !form.creditLedgerId ? "Select a ledger" : sameLedger ? "Debit and credit ledgers must be different" : "",
    amount: !String(form.amount).trim() ? "Enter an amount" : !amountOk(form.amount) ? "Enter an amount greater than 0 with at most 2 decimals" : "",
  };
  const serverFields = mapLineError(error?.fieldErrors || {});
  const formValid = Object.values(clientErrors).every((message) => !message) && Number(lines[0].debit) === Number(lines[1].credit);
  const show = (key) => (touched ? clientErrors[key] : "") || (key === "debitLedgerId" ? serverFields.debit : "") || (key === "creditLedgerId" ? serverFields.credit : "") || (key === "amount" ? serverFields.amount : "");

  const clear = () => {
    setForm({
      voucherDate: todayInput(),
      referenceNo: "",
      paymentMode: "",
      narration: "",
      debitLedgerId: "",
      creditLedgerId: "",
      amount: "",
    });
    setSaved(null);
    setTouched(false);
    setError(null);
  };

  const save = async (event) => {
    event.preventDefault();
    setTouched(true);
    setError(null);
    if (!formValid) return;
    setSaving(true);
    const body = {
      voucherType,
      voucherDate: form.voucherDate,
      referenceNo: form.referenceNo.trim(),
      narration: form.narration.trim(),
      lines: lines.map(({ ledgerId, debit, credit }) => ({ ledgerId, debit, credit })),
    };
    if (config.modeLabel) body.paymentMode = form.paymentMode;
    try {
      const result = await accountsApi.saveVoucher(body);
      setSaved(result.data);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const locked = Boolean(saved);

  return (
    <PageShell title={config.title} subtitle={`${config.label} entry · double-entry, posted on save`}>
      {saved && (
        <div className="accounts-success">
          <span>Voucher <strong>{saved.voucherNo}</strong> posted for {formatINR(saved.totalDebit)}.</span>
          <button type="button" className="btn btn-sm btn-outline-success" onClick={() => navigate(`${AccountsConstants.VOUCHER_VIEW}?id=${saved._id || saved.id}`)}><i className="bi bi-eye" /> View</button>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clear}>New {config.label.toLowerCase()}</button>
          {voucherType === "PURCHASE" && settlementLink("PURCHASE", saved) && <button type="button" className="btn btn-sm btn-primary" onClick={() => navigate(settlementLink("PURCHASE", saved))}>Record payment</button>}
        </div>
      )}
      <ApiAlert error={error} />
      {metaError && <div className="accounts-error">{metaError}</div>}
      {serverFields.preview && <div className="accounts-error">{serverFields.preview}</div>}
      <form onSubmit={save} noValidate>
        <div className="accounts-entry">
        <div className="accounts-panel">
          <div className="accounts-entry-grid">
            <label className="tally-field">Voucher No
              <input className="form-control" readOnly value={saved?.voucherNo || ""} placeholder="Auto-generated on save" />
            </label>
            <label className="tally-field">Date *
              <input className={`form-control${show("voucherDate") ? " is-invalid" : ""}`} type="date" max={todayInput()} value={form.voucherDate} onChange={set("voucherDate")} disabled={locked} />
              {show("voucherDate") && <span className="accounts-hint tally-error">{show("voucherDate")}</span>}
            </label>
            <label className="tally-field">Reference No
              <input
                className="form-control"
                maxLength={50}
                value={form.referenceNo}
                placeholder={linkedFrom === "invoice" ? "Invoice number" : linkedFrom === "purchase" ? "Purchase number" : voucherType === "RECEIPT" ? "Enter the invoice or bill number" : voucherType === "PAYMENT" ? "Enter the purchase number" : "Optional reference"}
                onChange={set("referenceNo")}
                disabled={locked}
              />
              {linkedFrom === "invoice" && <span className="accounts-hint">Filled from the sales invoice. Change it if this receipt covers a different bill.</span>}
              {linkedFrom === "purchase" && <span className="accounts-hint">Filled from the purchase voucher. Change it if this payment covers a different bill.</span>}
            </label>
            {config.modeLabel && (
              <label className="tally-field">{config.modeLabel} *
                <select className={`form-select${show("paymentMode") ? " is-invalid" : ""}`} value={form.paymentMode} onChange={set("paymentMode")} disabled={locked}>
                  <option value="">Select</option>
                  {(meta?.paymentModes || []).filter((mode) => ["BANK", "CASH", "CHEQUE", "UPI", "NEFT", "RTGS"].includes(mode.value)).map((mode) => <option key={mode.value} value={mode.value}>{mode.label}</option>)}
                </select>
                {show("paymentMode") && <span className="accounts-hint tally-error">{show("paymentMode")}</span>}
              </label>
            )}
            <LedgerCombo label={config.debit.label} required disabled={locked} groups={meta?.ledgerGroups || []} ledgers={debitLedgers} value={form.debitLedgerId}
              onChange={(id) => setForm((current) => ({ ...current, debitLedgerId: id }))} error={show("debitLedgerId")} />
            <LedgerCombo label={config.credit.label} required disabled={locked} groups={meta?.ledgerGroups || []} ledgers={creditLedgers} value={form.creditLedgerId}
              onChange={(id) => setForm((current) => ({ ...current, creditLedgerId: id }))} error={show("creditLedgerId")} />
            <label className="tally-field">Amount (₹) *
              <input className={`form-control${show("amount") ? " is-invalid" : ""}`} value={form.amount} placeholder="0.00" onChange={set("amount")} disabled={locked} inputMode="decimal" />
              <span className="accounts-hint">{show("amount") || (amountOk(form.amount) ? formatINR(form.amount) : " ")}</span>
            </label>
            <label className="tally-field accounts-span">Narration
              <textarea className="form-control" rows={2} maxLength={500} value={form.narration} placeholder="What this voucher is for" onChange={set("narration")} disabled={locked} />
              <span className="accounts-hint">{form.narration.length}/500</span>
            </label>
          </div>
        </div>
        <div className="accounts-panel">
          <h3>Accounting entry preview</h3>
          <EntryPreview lines={lines} />
        </div>
        </div>
        {!locked && (
          <div className="accounts-form-actions">
            <button className="btn btn-outline-secondary" type="button" onClick={clear} disabled={saving}>Clear</button>
            <button className="btn tally-btn" type="submit" disabled={saving}>{saving ? "Saving…" : `Save ${config.label.toLowerCase()}`}</button>
          </div>
        )}
      </form>
    </PageShell>
  );
}

export function EntryPreview({ lines }) {
  const debits = (lines || []).filter((line) => Number(line.debit) > 0);
  const credits = (lines || []).filter((line) => Number(line.credit) > 0);
  const totalDebit = debits.reduce((sum, line) => sum + Number(line.debit), 0);
  const totalCredit = credits.reduce((sum, line) => sum + Number(line.credit), 0);
  return (
    <div className="accounts-preview-table">
      <div className="accounts-preview-row head"><span>Particulars</span><span /><span>Debit</span><span>Credit</span></div>
      {debits.map((line, index) => (
        <div className="accounts-preview-row" key={`d${index}`}>
          <span>{line.ledgerName || "—"} A/c</span><span>Dr</span><span>{formatINR(line.debit)}</span><span />
        </div>
      ))}
      {credits.map((line, index) => (
        <div className="accounts-preview-row" key={`c${index}`}>
          <span className="indent">To {line.ledgerName || "—"} A/c</span><span /><span /><span>{formatINR(line.credit)}</span>
        </div>
      ))}
      <div className="accounts-preview-row total"><span>Total</span><span /><span>{formatINR(totalDebit)}</span><span>{formatINR(totalCredit)}</span></div>
    </div>
  );
}
